// ======================================================================
// FILE: src/modules/wms/checklist/components/ChecklistItemsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Checklist Item Management
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsChecklistService
from "../../services/wmsChecklistService";

import ChecklistItemOptionsTab
from "./ChecklistItemOptionsTab";


const DEFAULT_FORM = {

  sequence_no: "",

  item_title: "",
  item_description: "",

  response_type: "TEXT",

  mandatory: true,

  is_na_allowed: false,

  max_score: null,

  expected_value: "",

  allow_attachment: false,

  allow_remarks: true,

  auto_create_observation_on_fail: false,

  remarks: "",
};

const RESPONSE_TYPES = [
  "YES_NO",
  "PASS_FAIL",
  "TEXT",
  "NUMBER",
  "DATE",
  "TIME",
  "DROPDOWN",
  "PHOTO",
  "SIGNATURE",
  "QR_SCAN",
];

export default function ChecklistItemsTab({
  sectionId,
  sectionName,
}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showModal, setShowModal] =
    useState(false);

  const [editingItem, setEditingItem] =
    useState(null);

    const [selectedItem, setSelectedItem] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  useEffect(() => {

    if (sectionId) {
      loadItems();
    }

  }, [sectionId]);

  async function loadItems() {

    try {

      setLoading(true);

      const data =
        await wmsChecklistService
          .getItems(sectionId);

      setRows(data || []);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load items"
      );

    } finally {

      setLoading(false);
    }
  }

  function handleAdd() {

    setEditingItem(null);

    setForm(DEFAULT_FORM);

    setShowModal(true);
  }

  function handleEdit(row) {

    setEditingItem(row);

    setForm({

      sequence_no:
        row.sequence_no || "",

      item_title:
        row.item_title || "",

      item_description:
        row.item_description || "",

      response_type:
        row.response_type || "TEXT",

      mandatory:
        row.mandatory ?? true,

      is_na_allowed:
        row.is_na_allowed ?? false,

      max_score:
        row.max_score || "",

      expected_value:
        row.expected_value || "",

      allow_attachment:
        row.allow_attachment ?? false,

      allow_remarks:
        row.allow_remarks ?? true,

      auto_create_observation_on_fail:
        row.auto_create_observation_on_fail
        ?? false,

      remarks:
        row.remarks || "",
    });

    setShowModal(true);
  }

  async function handleSave() {

    if (!form.sequence_no) {

      alert(
        "Sequence No is required"
      );

      return;
    }

    if (!form.item_title) {

      alert(
        "Question is required"
      );

      return;
    }

    if (!form.response_type) {

      alert(
        "Response Type is required"
      );

      return;
    }

    try {

      if (editingItem) {

        const payload = {

            ...form,

            max_score:
                form.max_score === ""
                ? null
                : Number(form.max_score),

            expected_value:
                form.expected_value === ""
                ? null
                : form.expected_value,
            };

            await wmsChecklistService
            .updateItem(
                sectionId,
                editingItem.id,
                payload
            );

      } else {

        const payload = {

            ...form,

            max_score:
                form.max_score === ""
                ? null
                : Number(form.max_score),

            expected_value:
                form.expected_value === ""
                ? null
                : form.expected_value,
            };

            await wmsChecklistService
            .createItem(
                sectionId,
                payload
            );
      }

      setShowModal(false);

      await loadItems();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete item "${row.item_title}" ?`
    );

    if (!ok) {
      return;
    }

    try {

      await wmsChecklistService
        .deleteItem(
          sectionId,
          row.id
        );

      await loadItems();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  function supportsOptions(type) {

    return [
      "YES_NO",
      "PASS_FAIL",
      "DROPDOWN",
    ].includes(type);
  }

  return (

    <div className="card mt-3">

      <div
        className="
          card-header
          d-flex
          justify-content-between
          align-items-center
        "
      >

        <strong>
          Checklist Items
          {" "}
          -
          {" "}
          {sectionName}
        </strong>

        <button
          className="btn btn-sm btn-primary"
          onClick={handleAdd}
        >
          Add Item
        </button>

      </div>

      <div className="card-body p-0">

        <table
          className="
            table
            table-striped
            table-hover
            mb-0
          "
        >

          <thead>

            <tr>

              <th>Seq</th>

              <th>Question</th>

              <th>Response Type</th>

              <th>Mandatory</th>

              <th>Score</th>

              <th width="260">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading && (

              <tr>
                <td
                  colSpan="6"
                  className="text-center"
                >
                  Loading...
                </td>
              </tr>

            )}

            {!loading &&
              rows.length === 0 && (

              <tr>
                <td
                  colSpan="6"
                  className="text-center"
                >
                  No Items Found
                </td>
              </tr>

            )}

            {!loading &&
              rows.map((row) => (

              <tr key={row.id}>

                <td>
                  {row.sequence_no}
                </td>

                <td>
                  {row.item_title}
                </td>

                <td>
                  {row.response_type}
                </td>

                <td>
                  {row.mandatory
                    ? "Yes"
                    : "No"}
                </td>

                <td>
                  {row.max_score}
                </td>

                <td>

                  {supportsOptions(
                    row.response_type
                  ) && (

                    <button
                        className="
                            btn
                            btn-sm
                            btn-warning
                            me-1
                        "
                        onClick={() =>
                            setSelectedItem(row)
                        }
                        >
                        Options
                        </button>

                  )}

                  <button
                    className="
                      btn
                      btn-sm
                      btn-primary
                      me-1
                    "
                    onClick={() =>
                      handleEdit(row)
                    }
                  >
                    Edit
                  </button>

                  <button
                    className="
                      btn
                      btn-sm
                      btn-danger
                    "
                    onClick={() =>
                      handleDelete(row)
                    }
                  >
                    Delete
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {showModal && (

        <div
          className="modal d-block"
        >

          <div className="modal-dialog modal-xl">

            <div className="modal-content">

              <div className="modal-header">

                <h5>

                  {editingItem
                    ? "Edit Item"
                    : "Add Item"}

                </h5>

              </div>

              <div className="modal-body">

                <div className="row">

                  <div className="col-md-2 mb-3">

                    <label>
                      Sequence *
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={
                        form.sequence_no
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          sequence_no:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-6 mb-3">

                    <label>
                      Question *
                    </label>

                    <input
                      className="form-control"
                      value={
                        form.item_title
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          item_title:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-4 mb-3">

                    <label>
                      Response Type *
                    </label>

                    <select
                      className="form-control"
                      value={
                        form.response_type
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          response_type:
                            e.target.value,
                        })
                      }
                    >
                      {RESPONSE_TYPES.map(
                        (x) => (
                          <option
                            key={x}
                            value={x}
                          >
                            {x}
                          </option>
                        )
                      )}
                    </select>

                  </div>

                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        </div>
              )}

      {selectedItem && (

        <ChecklistItemOptionsTab
          itemId={selectedItem.id}
          itemTitle={selectedItem.item_title}
        />

      )}

    </div>

  );
}